import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { DateInput } from './DateInput';
import { DateInfo } from '@/utils/date/types';
import { gregorianToHijri, hijriToGregorian } from '@/utils/date/conversion';
import { isValidHijriDate, isValidGregorianDate } from '@/utils/date/validation';

interface DateFieldsProps {
  value: DateInfo | undefined;
  onChange: (date: DateInfo | undefined) => void;
  error?: string;
  required?: boolean;
  showSwitchInLabel?: boolean;
  inlineSwitch?: boolean;
}

export function DateFields({
  value,
  onChange,
  error,
  required = false,
  showSwitchInLabel = false,
  inlineSwitch = false
}: DateFieldsProps) {
  const [isHijri, setIsHijri] = useState(false);
  const [dateValue, setDateValue] = useState({
    year: value?.year?.toString() || '',
    month: value?.month?.toString() || '',
    day: value?.day?.toString() || ''
  });

  const handleDateChange = (newValue: typeof dateValue) => {
    setDateValue(newValue);

    const isValid = isHijri ? 
      isValidHijriDate(newValue) : 
      isValidGregorianDate(newValue);

    if (!isValid) {
      onChange(undefined);
      return;
    }

    onChange({
      type: isHijri ? 'hijri' : 'gregorian',
      year: parseInt(newValue.year),
      month: parseInt(newValue.month),
      day: parseInt(newValue.day)
    });
  };

  const handleCalendarChange = (useHijri: boolean) => {
    if (!dateValue.year || !dateValue.month || !dateValue.day) {
      setIsHijri(useHijri);
      return;
    }

    try {
      const newDate = useHijri ? 
        gregorianToHijri(dateValue) : 
        hijriToGregorian(dateValue);

      if (newDate) {
        setDateValue({
          year: newDate.year.toString(),
          month: newDate.month.toString(),
          day: newDate.day.toString()
        });
        setIsHijri(useHijri);
      }
    } catch (error) {
      console.error('Date conversion error:', error);
      setIsHijri(useHijri);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>تاريخ الميلاد {required && '*'}</Label>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-gray-500">
            {isHijri ? 'هجري' : 'ميلادي'}
          </Label>
          <Switch
            checked={isHijri}
            onCheckedChange={handleCalendarChange}
          />
        </div>
      </div>

      <DateInput
        value={dateValue}
        onChange={handleDateChange}
        isHijri={isHijri}
      />

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}